import { useState, useEffect } from 'react'
import { templateApi, TemplateFormConfig } from '@/services/templateApi'

export const useTemplateForm = () => {
  const [formConfig, setFormConfig] = useState<TemplateFormConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentTemplate, setCurrentTemplate] = useState<any>(null)

  useEffect(() => {
    loadFormConfig()
    
    // Listen for template change events
    const handleTemplateChange = () => {
      loadFormConfig()
    }
    
    window.addEventListener('templateChanged', handleTemplateChange)
    
    return () => {
      window.removeEventListener('templateChanged', handleTemplateChange)
    }
  }, [])

  const loadFormConfig = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Get current template and form config
      const [template, config] = await Promise.all([
        templateApi.getUserCurrentTemplate(),
        templateApi.getUserFormConfig()
      ])
      
      setCurrentTemplate(template)
      setFormConfig(config)
    } catch (err) {
      console.error('Error loading template form config:', err)
      setError('Failed to load form configuration')
    } finally {
      setLoading(false)
    }
  }

  const refreshConfig = () => {
    loadFormConfig()
  }

  // Helper functions for form rendering
  const getFieldConfig = (fieldName: string) => {
    return formConfig?.specifications.fields.find(field => field.name === fieldName)
  }

  const hasField = (fieldName: string) => {
    return formConfig?.specifications.fields.some(field => field.name === fieldName) ?? false
  }

  const getFieldOptions = (fieldName: string) => {
    const field = getFieldConfig(fieldName)
    // Always use field options if they exist (backend is source of truth)
    if (field?.options) return field.options

    // Fallback to industry standards only if field doesn't have options
    switch (fieldName) {
      case 'color':
        return formConfig?.industryStandards.colors || []
      case 'finish':
        return formConfig?.industryStandards.finishes || []
      case 'grade':
        return formConfig?.industryStandards.grades || []
      case 'manufacturer':
        return formConfig?.industryStandards.brands || []
      case 'style':
        return formConfig?.industryStandards.styles || []
      default:
        return []
    }
  }

  const getFieldLabel = (fieldName: string) => {
    const field = getFieldConfig(fieldName)
    return field?.label || fieldName
  }

  const getFieldPlaceholder = (fieldName: string) => {
    const field = getFieldConfig(fieldName)
    return field?.placeholder || `Enter ${fieldName}...`
  }

  const isFieldRequired = (fieldName: string) => {
    const field = getFieldConfig(fieldName)
    return field?.required || false
  }

  return {
    formConfig,
    loading,
    error,
    currentTemplate,
    refreshConfig,
    // Helper functions
    getFieldConfig,
    hasField,
    getFieldOptions,
    getFieldLabel,
    getFieldPlaceholder,
    isFieldRequired
  }
}